import { Link } from 'react-router';
import { Popover, PopoverContent, PopoverTrigger } from '@/components/ui/popover';
import { useEscalationPolicy } from '@/hooks/use-escalation-policy';
import {
  ASSIGN_TARGET_FIELDS,
  ASSIGN_TARGET_LABELS,
  ESCALATION_FIELDS,
  ESCALATION_TYPE_LABELS,
  FIELD_LABELS,
  type AssignTarget,
  type EscalationType
} from './escalation-policy';

/** A field's value as a person reads it: a target by its label. */
const display = (field: string, fields: Record<string, string>): string => {
  const value = fields[field] ?? '';
  return field === 'assignTarget' && (value === 'agent' || value === 'sentinel') ? ASSIGN_TARGET_LABELS[value] : value;
};

const capitalize = (text: string) => text.charAt(0).toUpperCase() + text.slice(1);

const EMAIL_OF: Record<string, string> = { agentName: 'agentEmail', sentinelUserName: 'sentinelUserEmail' };

/** A person's name, with their email when it says something the name does not. */
const withEmail = (field: string, fields: Record<string, string>): string => {
  const key = EMAIL_OF[field];
  const email = key ? fields[key] : undefined;
  return email && email !== fields[field] ? `${fields[field]} (${email})` : display(field, fields);
};

/**
 * "Brand policy": what this escalation falls back to for any field the step leaves blank, read
 * only, with the way to Settings → Escalations where it is edited.
 */
export function EscalationPolicyButton({
  type,
  target = 'agent',
  className = ''
}: {
  type: EscalationType;
  /** For an Assign: which target's default the step falls back to. */
  target?: AssignTarget;
  className?: string;
}) {
  const { data: policy, isLoading, isError } = useEscalationPolicy();
  const fields = policy?.[type] ?? {};
  const own = type === 'assign' ? ASSIGN_TARGET_FIELDS[target] : ESCALATION_FIELDS[type];
  // The person is one choice; their id is not something a person reads.
  const hidden = new Set(['agentId', 'agentEmail', 'agentProvider', 'sentinelUserId', 'sentinelUserEmail']);
  const rows = own.filter((field) => fields[field] && !hidden.has(field));
  const title = type === 'assign' ? `Assign to ${ASSIGN_TARGET_LABELS[target]}` : ESCALATION_TYPE_LABELS[type];

  return (
    <Popover>
      <PopoverTrigger asChild>
        <button
          type='button'
          className={`inline-flex h-6 shrink-0 cursor-pointer items-center rounded-full border border-gray-300 bg-white px-2.5 text-[11px] font-medium leading-4 text-gray-800 transition-colors hover:bg-gray-100 hover:text-ink ${className}`}
        >
          View brand policy
        </button>
      </PopoverTrigger>
      <PopoverContent align='end' className='z-[60] w-80 p-3 text-[12px] leading-4'>
        <p className='mb-1 font-semibold text-ink'>{title} · brand policy</p>
        <p className='mb-2 text-gray-800'>Used for any field this step leaves blank.</p>
        {isLoading ? (
          <p className='text-gray-800'>Loading…</p>
        ) : isError ? (
          <p className='text-red-600'>The brand policy could not be loaded.</p>
        ) : rows.length === 0 ? (
          <p className='text-gray-800'>No policy set for this brand — every field must be filled on the step.</p>
        ) : (
          <dl className='flex flex-col gap-1.5'>
            {rows.map((field) => (
              <div key={field}>
                <dt className='text-gray-800'>{capitalize(FIELD_LABELS[field] ?? field)}</dt>
                <dd className='max-h-24 overflow-y-auto whitespace-pre-wrap break-words text-ink'>
                  {withEmail(field, fields)}
                </dd>
              </div>
            ))}
          </dl>
        )}
        <Link to='/settings/escalations' className='mt-3 inline-block font-medium text-ink underline'>
          Edit in Escalations settings
        </Link>
      </PopoverContent>
    </Popover>
  );
}
